import type { LoadedSize } from "src/pages/ad-studio/insights/performance/types";

/**
 * Read off the rendered media element because the warehouse can't answer it:
 * neither Fivetran's Meta tables nor `ad_media_urls` store a width or height.
 * Called once the element has loaded, so a zero on either axis means a file the
 * browser could not decode, not one still loading.
 */
export function readMediaSize(
  element: HTMLImageElement | HTMLVideoElement,
): LoadedSize {
  const [width, height] =
    "videoWidth" in element
      ? [element.videoWidth, element.videoHeight]
      : [element.naturalWidth, element.naturalHeight];
  return width > 0 && height > 0
    ? { kind: "measured", width, height }
    : { kind: "load-failed" };
}

/** "Simple" is a ratio worth showing a marketer: `9:16` tells them what will be
 * built, the `300:157` a resized creative reduces to tells them nothing. */
type CreativeAspectRatio =
  | { kind: "simple"; label: string }
  | { kind: "not-simple" };

/** The shapes marketers buy all reduce well under this; above it means the asset
 * was resized off-ratio. */
const MAX_RATIO_TERM = 32;

export function simpleAspectRatio(dimensions: {
  width: number;
  height: number;
}): CreativeAspectRatio {
  const divisor = greatestCommonDivisor(dimensions.width, dimensions.height);
  const width = dimensions.width / divisor;
  const height = dimensions.height / divisor;
  return width <= MAX_RATIO_TERM && height <= MAX_RATIO_TERM
    ? { kind: "simple", label: `${width}:${height}` }
    : { kind: "not-simple" };
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}
